'use client';

import { c as _c } from "react/compiler-runtime";
import type { KeyboardEvent } from 'react';
import { useRef } from 'react';
import { Link } from '@/app/components/navigation/link';
import glossIndicatorStyles from '@/app/lcv/components/sections/spartan/shared/tabs/gloss-indicator.module.css';
import indicatorStyles from '@/app/lcv/components/sections/spartan/shared/tabs/indicator.module.css';
import { SpartanTabsIndicatorDarkVector } from '@/app/lcv/components/sections/spartan/shared/tabs/indicator-dark-vector';
import { SPARTAN_TABS_DARK_TRACK_STROKE_CLASS_NAME, SpartanTabsIndicatorStyled, SpartanTabsItemStyled, SpartanTabsStyled } from '@/app/lcv/components/sections/spartan/shared/tabs/styles';
import { SPARTAN_TABS_TRACKING } from '@/app/lcv/components/sections/spartan/shared/tabs/tracking';
import type { SpartanTabsGlossIndicatorTone, SpartanTabsGlowIndicatorTone, SpartanTabsIndicatorKind, SpartanTabsIndicatorTone, SpartanTabsProps, SpartanTabsVariant } from '@/app/lcv/components/sections/spartan/shared/tabs/types';
import { usePointerGlow } from '@/app/lcv/components/sections/spartan/shared/tabs/usePointerGlow';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';
export type { SpartanTabsGlossIndicatorTone, SpartanTabsGlowIndicatorTone, SpartanTabsIndicatorKind, SpartanTabsIndicatorTone, SpartanTabsItem, SpartanTabsProps, SpartanTabsVariant } from '@/app/lcv/components/sections/spartan/shared/tabs/types';
const GLOW_TRACK_VARIANTS = new Set<SpartanTabsVariant>(['dark', 'dark-on-light']);
const GLOSS_TRACK_VARIANTS = new Set<SpartanTabsVariant>(['lime', 'mist']);
const GLOW_INDICATOR_TONES = ['orange', 'blue', 'white'] as const;
const GLOW_INDICATOR_TONE_CLASS_NAMES = {
  orange: indicatorStyles.orange,
  blue: indicatorStyles.blue,
  white: indicatorStyles.white
} as const satisfies Record<SpartanTabsGlowIndicatorTone, string>;
const isGlowTone = (tone: SpartanTabsIndicatorTone): tone is SpartanTabsGlowIndicatorTone => tone === 'orange' || tone === 'blue' || tone === 'white';
const isGlossTone = (tone: SpartanTabsIndicatorTone): tone is SpartanTabsGlossIndicatorTone => tone === 'lime' || tone === 'mist';
const resolveIndicatorKind = (variant: SpartanTabsVariant, indicatorKind?: SpartanTabsIndicatorKind): SpartanTabsIndicatorKind | undefined => {
  if (indicatorKind) return indicatorKind;
  if (GLOSS_TRACK_VARIANTS.has(variant)) return 'gloss';
  if (GLOW_TRACK_VARIANTS.has(variant)) return 'glow';
  return undefined;
};
export const SpartanTabs = t0 => {
  const $ = _c(95);
  let ariaLabel;
  let as;
  let className;
  let indicatorKind;
  let itemClassName;
  let itemTestIdPrefix;
  let items;
  let onChange;
  let props;
  let selectedItemClassName;
  let t1;
  let t2;
  let t3;
  let t4;
  let tracking;
  let value;
  let wrapperClassName;
  if ($[0] !== t0) {
    ({
      items,
      value,
      onChange,
      variant: t1,
      indicatorKind,
      indicatorTone: t2,
      showTrackStroke: t3,
      className,
      wrapperClassName,
      itemClassName,
      selectedItemClassName,
      "aria-label": ariaLabel,
      as,
      hideWhenSingle: t4,
      tracking,
      itemTestIdPrefix,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = as;
    $[3] = className;
    $[4] = indicatorKind;
    $[5] = itemClassName;
    $[6] = itemTestIdPrefix;
    $[7] = items;
    $[8] = onChange;
    $[9] = props;
    $[10] = selectedItemClassName;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = tracking;
    $[16] = value;
    $[17] = wrapperClassName;
  } else {
    ariaLabel = $[1];
    as = $[2];
    className = $[3];
    indicatorKind = $[4];
    itemClassName = $[5];
    itemTestIdPrefix = $[6];
    items = $[7];
    onChange = $[8];
    props = $[9];
    selectedItemClassName = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    tracking = $[15];
    value = $[16];
    wrapperClassName = $[17];
  }
  const variant = t1 === undefined ? "filled" : t1;
  const indicatorTone = t2 === undefined ? "orange" : t2;
  const showTrackStroke = t3 === undefined ? true : t3;
  const hideWhenSingle = t4 === undefined ? true : t4;
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const tabRefs = useRef(t5);
  const selectedValue = value ?? items[0]?.value;
  let t6;
  if ($[19] !== indicatorKind || $[20] !== variant) {
    t6 = resolveIndicatorKind(variant, indicatorKind);
    $[19] = indicatorKind;
    $[20] = variant;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const resolvedIndicatorKind = t6;
  const isGlowIndicator = resolvedIndicatorKind === "glow";
  const isGlossIndicator = resolvedIndicatorKind === "gloss";
  const glowTone = isGlowTone(indicatorTone) ? indicatorTone : "orange";
  const t7 = isGlowIndicator && glowTone !== "white";
  let t8;
  if ($[22] !== selectedValue || $[23] !== t7) {
    t8 = {
      isEnabled: t7,
      resetKey: selectedValue
    };
    $[22] = selectedValue;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const {
    indicatorRef,
    isActive: isPointerGlowActive,
    pointerProps
  } = usePointerGlow(t8);
  if (!items.length || hideWhenSingle && items.length <= 1) {
    return null;
  }
  let t9;
  if ($[25] !== selectedValue) {
    t9 = item => item.value === selectedValue;
    $[25] = selectedValue;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const selectedIndex = items.findIndex(t9);
  const isSingle = items.length === 1;
  const glossTone = isGlossTone(indicatorTone) ? indicatorTone : variant === "mist" ? "mist" : "lime";
  const showDarkTrackStroke = showTrackStroke && variant === "dark";
  const showIndicator = selectedIndex >= 0 && (items.length > 1 || variant === "dark");
  const Root = as ?? (items.some(_temp) ? "nav" : "div");
  let t10;
  if ($[27] !== Root || $[28] !== items) {
    t10 = Root !== "nav" && !items.some(_temp2);
    $[27] = Root;
    $[28] = items;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const isTablist = t10;
  let t11;
  if ($[30] !== onChange || $[31] !== selectedValue) {
    t11 = itemValue => {
      if (itemValue === selectedValue) {
        return;
      }
      safeCall(onChange, itemValue);
    };
    $[30] = onChange;
    $[31] = selectedValue;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const handleSelect = t11;
  let T0;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[33] !== Root || $[34] !== ariaLabel || $[35] !== className || $[36] !== glossTone || $[37] !== glowTone || $[38] !== handleSelect || $[39] !== indicatorRef || $[40] !== indicatorTone || $[41] !== isGlossIndicator || $[42] !== isGlowIndicator || $[43] !== isPointerGlowActive || $[44] !== isSingle || $[45] !== isTablist || $[46] !== itemClassName || $[47] !== itemTestIdPrefix || $[48] !== items || $[49] !== pointerProps || $[50] !== props || $[51] !== resolvedIndicatorKind || $[52] !== selectedIndex || $[53] !== selectedItemClassName || $[54] !== selectedValue || $[55] !== showDarkTrackStroke || $[56] !== showIndicator || $[57] !== tracking || $[58] !== variant || $[59] !== wrapperClassName) {
    const focusTabAt = index => {
      const nextIndex = (index + items.length) % items.length;
      const nextItem = items[nextIndex];
      if (!nextItem) {
        return;
      }
      tabRefs.current[nextIndex]?.focus();
      handleSelect(nextItem.value);
    };
    const handleTabKeyDown = (event, index_0) => {
      bb98: switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          {
            event.preventDefault();
            focusTabAt(index_0 + 1);
            break bb98;
          }
        case "ArrowLeft":
        case "ArrowUp":
          {
            event.preventDefault();
            focusTabAt(index_0 - 1);
            break bb98;
          }
        case "Home":
          {
            event.preventDefault();
            focusTabAt(0);
            break bb98;
          }
        case "End":
          {
            event.preventDefault();
            focusTabAt(items.length - 1);
            break bb98;
          }
        default:
      }
    };
    T0 = Root;
    t12 = isTablist ? "tablist" : undefined;
    t13 = ariaLabel;
    const t19 = showDarkTrackStroke && SPARTAN_TABS_DARK_TRACK_STROKE_CLASS_NAME;
    if ($[68] !== className || $[69] !== t19 || $[70] !== variant || $[71] !== wrapperClassName) {
      t14 = SpartanTabsStyled({
        variant,
        className: [wrapperClassName, className, t19]
      });
      $[68] = className;
      $[69] = t19;
      $[70] = variant;
      $[71] = wrapperClassName;
      $[72] = t14;
    } else {
      t14 = $[72];
    }
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = createDataAttributes({
        test: SPARTAN_TABS_TRACKING.root
      });
      $[73] = t15;
    } else {
      t15 = $[73];
    }
    t16 = props;
    if ($[74] !== glossTone || $[75] !== glowTone || $[76] !== indicatorRef || $[77] !== isGlossIndicator || $[78] !== isGlowIndicator || $[79] !== isPointerGlowActive || $[80] !== items.length || $[81] !== resolvedIndicatorKind || $[82] !== selectedIndex || $[83] !== showIndicator || $[84] !== variant) {
      t17 = !!showIndicator && <span ref={indicatorRef} aria-hidden="true" data-indicator-kind={resolvedIndicatorKind} data-indicator-tone={isGlossIndicator ? glossTone : isGlowIndicator ? glowTone : undefined} className={cn(SpartanTabsIndicatorStyled({
        variant
      }), isGlowIndicator && "bg-transparent", isGlowIndicator && indicatorStyles.indicator, isPointerGlowActive && indicatorStyles.pointerGlow, isGlowIndicator && variant === "dark" && indicatorStyles.indicatorShadow, isGlossIndicator && glossIndicatorStyles.indicator, isGlossIndicator && glossTone === "mist" && glossIndicatorStyles.mist)} style={{
        width: `calc((100% - 0.5rem) / ${items.length})`,
        transform: `translate3d(${selectedIndex * 100}%, 0, 0)`
      }}>{isGlowIndicator ? GLOW_INDICATOR_TONES.map(paletteTone => <span key={paletteTone} data-indicator-palette={paletteTone} className={cn(indicatorStyles.palette, GLOW_INDICATOR_TONE_CLASS_NAMES[paletteTone], paletteTone === glowTone && indicatorStyles.paletteActive)}><span data-indicator-layer="face" className={indicatorStyles.face} /><SpartanTabsIndicatorDarkVector indicatorTone={paletteTone} /></span>) : null}</span>;
      $[74] = glossTone;
      $[75] = glowTone;
      $[76] = indicatorRef;
      $[77] = isGlossIndicator;
      $[78] = isGlowIndicator;
      $[79] = isPointerGlowActive;
      $[80] = items.length;
      $[81] = resolvedIndicatorKind;
      $[82] = selectedIndex;
      $[83] = showIndicator;
      $[84] = variant;
      $[85] = t17;
    } else {
      t17 = $[85];
    }
    t18 = items.map((item_2, index_1) => {
      const isSelected = item_2.value === selectedValue;
      const selectedPointerProps = isSelected && isPointerGlowActive ? pointerProps : {};
      const sharedClassName = cn(SpartanTabsItemStyled({
        variant,
        isSelected,
        isSingle,
        indicatorTone: isGlowIndicator ? glowTone : indicatorTone,
        className: [itemClassName, isSelected ? selectedItemClassName : ""]
      }), isSelected && isGlossIndicator && "text-gray-700");
      const trackingProps = tracking ? createDataGA({
        ...tracking,
        click: true,
        text: item_2.label,
        ...(item_2.href ? {
          url: item_2.href
        } : {})
      }) : {};
      const itemDataAttributes = createDataAttributes({
        test: itemTestIdPrefix ? `${itemTestIdPrefix}-${index_1 + 1}` : SPARTAN_TABS_TRACKING.item(index_1),
        selected: isSelected
      });
      if (item_2.href) {
        return <Link key={item_2.value} href={item_2.href} scroll={false} aria-current={isSelected ? "page" : undefined} className={sharedClassName} onClick={() => handleSelect(item_2.value)} {...selectedPointerProps} {...itemDataAttributes} {...trackingProps}><span className="relative z-2">{item_2.label}</span></Link>;
      }
      return <button key={item_2.value} ref={node => {
        tabRefs.current[index_1] = node;
      }} type="button" role={isTablist ? "tab" : undefined} id={isTablist ? item_2.tabId : undefined} aria-controls={isTablist ? item_2.panelId : undefined} aria-selected={isTablist ? isSelected : undefined} tabIndex={isTablist ? isSelected ? 0 : -1 : undefined} className={sharedClassName} onClick={() => handleSelect(item_2.value)} onKeyDown={isTablist ? event_0 => handleTabKeyDown(event_0, index_1) : undefined} {...selectedPointerProps} {...itemDataAttributes} {...trackingProps}><span className="relative z-2">{item_2.label}</span></button>;
    });
    $[33] = Root;
    $[34] = ariaLabel;
    $[35] = className;
    $[36] = glossTone;
    $[37] = glowTone;
    $[38] = handleSelect;
    $[39] = indicatorRef;
    $[40] = indicatorTone;
    $[41] = isGlossIndicator;
    $[42] = isGlowIndicator;
    $[43] = isPointerGlowActive;
    $[44] = isSingle;
    $[45] = isTablist;
    $[46] = itemClassName;
    $[47] = itemTestIdPrefix;
    $[48] = items;
    $[49] = pointerProps;
    $[50] = props;
    $[51] = resolvedIndicatorKind;
    $[52] = selectedIndex;
    $[53] = selectedItemClassName;
    $[54] = selectedValue;
    $[55] = showDarkTrackStroke;
    $[56] = showIndicator;
    $[57] = tracking;
    $[58] = variant;
    $[59] = wrapperClassName;
    $[60] = T0;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
  } else {
    T0 = $[60];
    t12 = $[61];
    t13 = $[62];
    t14 = $[63];
    t15 = $[64];
    t16 = $[65];
    t17 = $[66];
    t18 = $[67];
  }
  let t19;
  if ($[86] !== T0 || $[87] !== t12 || $[88] !== t13 || $[89] !== t14 || $[90] !== t15 || $[91] !== t16 || $[92] !== t17 || $[93] !== t18) {
    t19 = <T0 role={t12} aria-label={t13} className={t14} {...t15} {...t16}>{t17}{t18}</T0>;
    $[86] = T0;
    $[87] = t12;
    $[88] = t13;
    $[89] = t14;
    $[90] = t15;
    $[91] = t16;
    $[92] = t17;
    $[93] = t18;
    $[94] = t19;
  } else {
    t19 = $[94];
  }
  return t19;
};
function _temp(item_0) {
  return item_0.href;
}
function _temp2(item_1) {
  return item_1.href;
}